import { GRANULARITY } from "../ReadingsByDate";
import { DtConstants } from "../../schema/DtConstants";
import { dayjs } from "./time";

const { Intervals, Now } = DtConstants.Timeline;

/**
 * Desired number of timeline data subdivisions.
 *
 * Used to control the granularity of the data used in the timeline. The timeline will request data for the crudest
 * resolution that gives this number of subdivisions maxing out to raw values.
 * (ex: for the default 3 days, anything above 288 will result in RAW granularity).
 * @see TimeInterval.asGranularity
 */
const PTS_PER_INTERVAL = 500;

/** Window of time, allows both symbolic and explicit definitions. */
export class TimeInterval {
  /** @type {dayjs.Duration} Symbolic length of the interval */
  duration;
  /** @type {MaybeNowTS} Exclusive end to the interval */
  end;
  /** @type {string} Key for the interval */
  key;

  constructor(end, duration, key) {
    this.duration = duration;
    this.end = end;
    this.key = key;
  }

  /**
   * Interval of time covered, as DayJS datetimes.
   * @param {string} [tzId] TZ Database timezone identifier. Defaults to user's
   * @param {number} [now] Optional override on what is now, as a UNIX timestamp.
   */
  resolve(tzId, now = Date.now()) {
    const end = resolveSym(this.end, now);
    const beg = end - this.duration.asMilliseconds();
    return {
      beg: dayjs(beg).tz(tzId),
      end: dayjs(end).tz(tzId)
    };
  }

  /** Returns UTC date range in the form of [from, to) */
  asUTCDateRange(now = Date.now()) {
    const utcEnd = dayjs.utc(this.end === Now ? now : this.end);
    const utcBeg = utcEnd.subtract(this.duration);
    return [utcBeg, utcEnd.add(1, 'day')];
  }

  asGranularity() {
    const divisionInMs = this.duration.asMilliseconds() / PTS_PER_INTERVAL;

    // Choose the crudest granularity that matches this number of subdivisions.
    for (const key in GRANULARITY) {
      const granularity = GRANULARITY[key];
      if (divisionInMs > granularity) {
        return granularity;
      }
    }

    throw new Error('unmatched granularity');
  }

  contains(ts, now = Date.now()) {
    const end = resolveSym(this.end, now);
    if (ts >= end) return false;

    const beg = end - this.duration.asMilliseconds();
    return beg <= ts;
  }

  /**
   * Helper method to simplify reacting to updates.
   * @param {Map} lastByDbId Change in last readings by dbId.
   * @return {boolean} If any last reading is within the interval.
   */
  containsLastReadingEvent(lastByDbId) {
    // The for-loops are due to the object structure Map<dbId, Object.<aid, { ts: value }>> and won't be cubic.
    const now = Date.now();
    for (const byAid of lastByDbId.values()) {
      for (const aid in byAid) {
        for (const sec in byAid[aid]) {
          if (this.contains(Number(sec) * 1000, now)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  equals(interval) {
    return interval != null &&
    this.end === interval.end &&
    this.duration.asMilliseconds() === interval.duration.asMilliseconds();
  }

  /**
   * Creates a window based on one of the supported keys.
   * @param {String} key One of KEYS.
   * @param {any} [opt] Optional configuration for the interval.
   * @return {TimeInterval}
   */
  static Create(key, opt) {
    switch (key) {
      case Intervals.LAST_3_DAYS:
        return new TimeInterval(Now, dayjs.duration({ days: 3 }), key);
      case Intervals.LAST_7_DAYS:
        return new TimeInterval(Now, dayjs.duration({ days: 7 }), key);
      case Intervals.CUSTOM:
        return createCustomInterval(opt);
      default:
        console.warn('Unimplemented time window key: ' + key);
    }
  }
}

function createCustomInterval(options, now = Date.now()) {
  const isValid = options && options.end != null && (options.beg != null || options.duration != null);
  if (!isValid) throw new Error('invalid interval configuration');

  let { beg, duration, end } = options;
  if (end !== Now && typeof end !== 'number') {
    throw new Error('invalid interval end');
  }

  if (beg != null) {
    if (typeof beg !== 'number') throw new Error('invalid interval beg');
    duration = dayjs.duration(resolveSym(end, now) - beg);
  } else {
    if (dayjs.isDuration(duration)) {/* already good */} else
    if (typeof duration === 'number') duration = dayjs.duration(duration);else
    throw new Error('invalid interval duration');
  }

  if (duration.asMilliseconds() <= 0) {
    throw new Error('invalid reversed or empty interval');
  }

  return new TimeInterval(end, duration, Intervals.CUSTOM);
}

/**
 * Resolves potentially symbolic time. (caller must match time & now WRT sec vs ms)
 * @param {MaybeNowTS} time
 * @param {number} now (likely Date.now() or nowSec() whichever matches "time")
 * @return {number}
 */
export function resolveSym(time, now) {
  return time === Now ? now : time;
}